import { Locale } from "vue-i18n";

export type PurchasedProduct = {
    id: number;
    product: string;
    quantity: number;
    trainer: string;
};

export type Identifier = {
    id: number;
    identifier: string;
    texts: Text[];
};

export type Text = {
    id: number;
    text: string;
    language: string;
};

export type Product = {
    productNo: string;
    shopLink: string;
    active: boolean;
    model: Model;
};

export type Model = {
    model: string;
    rank: number;
};

export type TranslatedProduct = {
    productName: string;
    productNo: string;
    quantity: number;
    shopLink: string;
    modelName: string;
    modelNo: string;
    rank: number;
    profileLanguages: string[];
    questionnaireLanguages: string[];
};

/**
 * A group chart as the public Language Overview shows it (RDT-403). Group charts are no products,
 * so they carry no shop link or quantity; the model is the one of the products they aggregate.
 */
export type TranslatedGroupChart = {
    groupChartName: string;
    groupChartNo: string;
    modelName: string;
    modelNo: string;
    groupChartLanguages: string[];
};

export type LanguageProduct = {
    id: number;
    name: string;
    languages: string[];
};

export type ProductLanguages = { [key: string]: string[] };

export type Trainer = {
    company: string;
    language: string;
    recipient: string;
    distributor: Distributor;
    purchaseLimit: number;
    isDistributorAccount: boolean;
    email: string;
    firstname: string;
    lastname: string;
    address: Address | null;
    id: string;
    timeZone: string;
    isStockKeepingDistributor: boolean;
    portrait: string;
    logo: string;
    beta: boolean;
    standardRetentionPeriod: string;
    deletionNotification: boolean;
    /**
     * yyyy-MM-dd up to which this trainer was shown the release notes; null = not tracked yet.
     * Optional because client-side built trainer payloads (e.g. on creation) do not know it.
     */
    lastSeenReleaseNotesAt?: string | null;
};

export type Distributor = {
    id: number;
    company: string;
    monthlyBillingEnabled: boolean;
};

export type Address = {
    country: string;
    postalCode: string;
    city: string;
    street: string;
    website: string;
    phoneNo: string;
    id: number;
};

export type MonthlyPurchase = {
    id?: number;
    product: string;
    quantity: number;
    trainer: string;
    date: string;
};

export type SubAccountPurchasesPageRequest = {
    trainerId: string;
    from: string;
    to: string;
    page: number;
    size: number;
};

export type SubAccountPurchasesPageResponse = {
    purchases: MonthlyPurchase[];
    page: number;
    size: number;
    totalElements: number;
    totalQuantity: number;
    hasNext: boolean;
};

export type SubAccountsPageRequest = {
    trainerId: string;
    page: number;
    size: number;
    search?: string;
};

export type SubAccountsPageResponse = {
    trainers: Trainer[];
    page: number;
    size: number;
    totalElements: number;
};

export type Transaction = {
    id?: number;
    trainer: string;
    type: BalanceActivityType;
    date: string;
    quantity: number;
    product: Product;
    productDescription?: string;
    oldBalance: number;
    newBalance: number;
    // The other side's trainer id for two-party movements (transfers, sales, reclaims);
    // null for single-party types and rows written before RDT-113.
    counterparty?: string | null;
};

// Outcome of a distributor reclaiming profiles from a sub-account (RDT-113); the retrieved
// amount can be lower than requested when the sub-account's stock did not cover it.
export type ProfileRetrieval = {
    product: string;
    requestedQuantity: number;
    retrievedQuantity: number;
    subAccountBalance: number;
    distributorBalance: number;
};

export type ExtendedMonthlyPurchase = {
    id?: number;
    productDescription: string;
    quantity: number;
    trainer: string;
    email: string;
    date: string;
    manual: boolean;
    // Raw ids for the per-row reclaim action (RDT-113); only set when the row is a sub-account
    // acquisition the viewing distributor may reclaim from.
    trainerId?: string;
    productNo?: string;
};

export type MonthlyTransfer = {
    id?: number;
    product: string;
    quantity: number;
    trainer: string;
    recipient: string;
    date: string;
    manual: boolean;
    source?: string;
};

export type ExtendedMonthlyTransfer = {
    id?: number;
    productDescription: string;
    quantity: number;
    trainer: string;
    recipient: string;
    email: string;
    date: string;
    manual: boolean;
    source?: string;
};

/**
 * How an anonymous project gets its participants' pseudonyms. Ignored unless `Project.anonymous`.
 *
 * SELF_CHOSEN is the original behaviour: the participant types a pseudonym. It stays available for
 * studies, where the participant applies a reproducible rule (for example two letters of their
 * mother's first name plus their day of birth) so a follow-up survey can be matched to the same
 * person without identifying them. GENERATED means the backend assigns a code and the participant
 * never sees the field.
 */
export enum PseudonymMode {
    SELF_CHOSEN = "SELF_CHOSEN",
    GENERATED = "GENERATED",
}

export enum Recipients {
    TRAINER = "TRAINER",
    PARTICIPANT = "PARTICIPANT",
    BOTH = "BOTH",
}

export enum Rights {
    PROFILES = "PROFILES",
    PROJECTS = "PROJECTS",
}

export type Participants = {
    participants: Participant[];
};

export type Participant = {
    id?: number;
    status?: ParticipantStatus;
    gender?: string;
    email: string;
    firstName: string;
    name: string;
    questionnaire?: string;
    projectId: number;
    selectedProduct: string;
    departmentId?: number | null;
    roleId?: number;
    deletionDate?: string | null;
    score?: number;
    /** Marks pseudonymous data for life — stays true after the project's de-anonymization. */
    anonymous?: boolean;
};

export enum ParticipantStatus {
    CREATED = "CREATED",
    ACTIVATED = "ACTIVATED",
    STARTED = "STARTED",
    COMPLETED = "COMPLETED",
    CONVERTED = "CONVERTED",
    CALCULATED = "CALCULATED",
    FILLED = "FILLED",
    GENERATED = "GENERATED",
    NOTIFIED = "NOTIFIED",
    REVOKED = "REVOKED",
    DELETED = "DELETED",
    ARCHIVED = "ARCHIVED",
}

export type GroupChart = {
    products: Products;
    groupChart: string;
};

export type GroupChartSummary = {
    groupChartId: number;
    projectId: number;
    label: string;
    product: string | null;
    participantIds: number[];
    languages: string[];
    createdAt: string;
    /** The PDFs were deleted because a member's data was deleted or revoked (RDT-475). */
    pdfDeleted?: boolean;
    /** Why a chart whose PDFs were deleted cannot be created again; null when it can. */
    regenerationProblem?: GroupChartRegenerationProblem | null;
};

export type GroupChartRegenerationProblem =
    | "NO_PARTICIPANTS"
    | "FEEDBACK_RECIPIENT_MISSING"
    | "FEEDBACK_RECIPIENT_MULTIPLE"
    | "FEEDBACK_GIVERS_MISSING";

export type Products = {
    products: Product[];
};

/**
 * Participants role e.g. for in129
 */
export type ProjectRole = {
    id?: number;
    role: string;
    name: string;
};

export type Role = {
    product: string;
    type: string;
};

export type ParticipantActivity = {
    id?: number;
    type: string;
    date: string;
    /** Only set for PROFILE_GENERATED: the language the profile was generated in (e.g. "de"). */
    profileLanguage?: string | null;
};

// One shared declaration in @persolog/utils, re-exported so existing imports keep working: three
// copies of this enum are what let the frontends drift out of sync with the backend (RDT-389).
export { ParticipantActivityType } from "@persolog/utils";

export enum BalanceActivityType {
    PURCHASE_MONTHLY_BILLING,
    PURCHASE_SHOP,
    CANCELLATION_SHOP,
    USE_PROFILE,
    REFUND_PROFILE,
    SUPPORT,
    SALE_TO_SUB_ACCOUNT,
    PURCHASE_FROM_DISTRIBUTOR,
    TRANSFER_SENT,
    TRANSFER_RECEIVED,
    RETRIEVAL_FROM_SUB_ACCOUNT,
    RETRIEVAL_BY_DISTRIBUTOR,
}


export type Project = {
    id?: number;
    name: string;
    description?: string;
    product: string;
    defaultProduct: string;
    language: Locale;
    trainer?: string;
    anonymous?: boolean;
    focus?: string;
    recipients?: Recipients;
    additionalRecipient?: Recipient | null;
    status?: string;
    creation?: string;
    editedDate?: string;
    archiveDate?: string;
    deletionDate?: string | null;
    participantDeletionDate?: string | null;
    participantRetentionPeriod: string;
    participantDeletionNotification: boolean;
    recipientReleaseDate?: string | null;
    /** Only meaningful when `anonymous` is true. Fixed once a participant has registered. */
    pseudonymMode?: PseudonymMode;
};

/**
 * Lightweight "surface" view of a project used to render the paged project overview without loading
 * core data (participants, roles, departments). Mirrors the backend ProjectSurface DTO.
 */
export type ProjectSurface = {
    id: number;
    name: string;
    product: string;
    defaultProduct: string;
    language: Locale;
    status?: string;
    recipients?: Recipients;
    additionalRecipient?: Recipient | null;
    focus?: string;
    anonymous?: boolean;
    creation?: string;
    editedDate?: string;
    archiveDate?: string | null;
    deletionDate?: string | null;
    participantCount: number;
    /**
     * The direct-link summary for direct-link projects (null/absent otherwise), embedded so the
     * overview can show the used/total ratio and the details view can build/edit the link without a
     * separate request. Depletion is derived from participantCount.
     */
    directLink?: DirectLinkSummary | null;
    /**
     * Carried on the surface because the project overview hands surface-backed projects to the
     * settings dialog; without it the pseudonym radio has nothing to preselect from.
     */
    pseudonymMode?: PseudonymMode;
};

/** Card-relevant part of a direct link embedded in a {@link ProjectSurface}. */
export type DirectLinkSummary = {
    id: number;
    quantity: number;
    hash: string;
    /** Self-hosted short URL for this direct link (go.persolog.online/...), if one was created. */
    shortUrl?: string | null;
    /** ISO instant when the short link expires, if an expiry was set. */
    shortLinkExpiresAt?: string | null;
    /** ISO instant at which the direct link is deleted automatically, if set. */
    expiresAt?: string | null;
};

/** A single page of project surfaces together with the paging metadata from the backend. */
export type PagedProjects = {
    projects: ProjectSurface[];
    page: number;
    size: number;
    totalElements: number;
    hasArchived?: boolean;
};

/** Parameters accepted by the paged project surface endpoint. */
export type ProjectsPageRequest = {
    trainerId: string;
    page: number;
    size: number;
    sort: "creation" | "edited";
    direction: "asc" | "desc";
    products?: string[];
    search?: string;
    from?: string;
    to?: string;
    archived?: boolean;
};

export type Department = {
    id?: number | null;
    name: string;
};

export type Dependency = {
    product: string;
    dependencies: string[];
};

export type Recipient = {
    email: string;
    firstName: string;
    lastName: string;
};

export type Tag = {
    id?: number;
    value?: string;
};

export type Tags = {
    tags?: Tag[];
};

export type DirectLink = {
    id?: number;
    project: Project;
    quantity: number;
    hash?: string;
    depleted?: boolean;
    /** Self-hosted short URL (go.persolog.online/...), if a short link has been created. */
    shortUrl?: string | null;
    /** ISO instant when the short link expires, if an expiry was set. */
    shortLinkExpiresAt?: string | null;
    /** ISO instant at which the direct link is deleted automatically, if set. */
    expiresAt?: string | null;
};

/** Request body for creating (or replacing, on rename) a direct link's short link. */
export type ShortLinkRequest = {
    /** The exact long direct-link URL the short link should redirect to (built by the frontend). */
    targetUrl: string;
    /** Optional human-chosen slug for a named short link; omit for an auto-generated one. */
    customSlug?: string;
    /** Optional ISO instant after which the short link stops resolving. */
    expiresAt?: string;
};

export type QuestionnaireParticipant = {
    id: number;
    hash: string;
    trainer: string;
    status: string;
    focus: string;
};

export type Questionnaire = {
    id: string;
    hasFocus: boolean;
};

export type AssociatedAccount = {
    id?: number;
    trainer: Trainer;
    associatedAccount: Trainer;
    rights: string;
};

export interface ValidateableComponent {
    validate: () => boolean;
}

export interface StepNavItem {
    label: string;
    icon?: string;
    disabled: boolean;
    props: Record<string, unknown>;
    [key: string]: unknown;
}

export type LinkedAccountData = {
    id?: number;
    trainerEmail: string;
    associatedAccountId: string;
    rights: string;
};

export interface AssociatedDirectInput {
    id: number;
    input: DirectInput;
}

export interface DirectInput {
    product: string;
    dimensions: Dimension[];
}

export interface Dimension {
    id: string;
    name: string;
    competencies: Competency[];
    sumConstraint: string;
    max: number;
}

export interface Competency {
    id: string;
    name: string;
    values: number[];
    min: number;
    max: number;
}

export interface ProductCalculationResults {
    id: number;
    calculations: ProductCalculationResult[];
}

export interface ProductCalculationResult {
    product: string;
    dimensionCalculationResults: DimensionCalculationResult[];
    level: number;
    sum: number;
}

export interface DimensionCalculationResult {
    identifier: string;
    dimension: string;
    competencyCalculationResults: CompetencyCalculationResult[];
    level: number;
    sum: number;
}

export interface CompetencyCalculationResult {
    identifier: string;
    competency: string;
    level: number;
    sum: number;
}

export type Email = {
    type: EmailType;
    profile: string;
    link: string;
    trainer: EmailTrainer;
    participant?: EmailParticipant; // Optional because of the default value
    additionalRecipient?: EmailRecipient; // Optional because of the default value
    profileRecipient: ProfileRecipient;
    /**
     * Who this email is about (RDT-365), so support can tie its delivery status to the right
     * person. Optional: without it the e-mail-service can only recognise the email again by its
     * recipient address, which cannot tell two participants sharing an address apart.
     */
    reference?: EmailReference;
};

/**
 * Always identifies the *recipient*, mirroring how the e-mail-service routes each type. Referencing
 * a participant on an email that goes to the trainer would file it under the participant while
 * showing the trainer's address.
 */
export type EmailReference = {
    type: EmailReferenceType;
    id: string;
};

export enum EmailReferenceType {
    PARTICIPANT = "PARTICIPANT",
    TRAINER = "TRAINER",
}

export type EmailTrainer = {
    firstname: string;
    lastname: string;
    company: string;
    email: string;
};

export type EmailRecipient = {
    firstname: string;
    lastname: string;
    email: string;
};

export type EmailParticipant = {
    firstname: string;
    lastname: string;
    email: string;
};

export type CrossSell = {
    products: string[];
    crossSellProducts: CrossSellProduct[];
};

export type CrossSellProduct = {
    product: string;
    picture: string;
};

export type ClientInfo = {
    browserName: string;
    browserVersion: string;
    osName: string;
    osVersion: string
}

export enum EmailType {
    QUESTIONNAIRE = "QUESTIONNAIRE",
    TRAINER_PORTAL = "TRAINER_PORTAL",
    PARTICIPANT_PORTAL = "PARTICIPANT_PORTAL",
    ADDITIONAL_RECIPIENT = "ADDITIONAL_RECIPIENT",
    DIRECT_LINK = "DIRECT_LINK",
    NEW_ACCOUNT = "NEW_ACCOUNT",
    ORDER = "ORDER",
    GROUP_CHART = "GROUP_CHART",
    SELF_EVALUATION_DOI = "SELF_EVALUATION_DOI",
}

export enum ProfileRecipient {
    TRAINER = "TRAINER",
    PARTICIPANT = "PARTICIPANT",
    BOTH = "BOTH",
}

export type Language = "de" | "en" | "fr" | "es" | "nl" | "no" | "ro" | "pl" | "it" | "sl" | "da" | "hu" | "hr";

/** A customer-facing release note shown on the public release-notes page (RDT-286). */
export interface ReleaseNote {
    issueKey: string;
    kind: "FEATURE" | "FIX";
    /** yyyy-MM-dd — when the change first reached customers. */
    releasedOn: string;
    noteDe: string;
    noteEn: string;
}

/**
 * One calendar month of notes. Releases are frequent and per-app, so the page groups by the month a
 * change reached customers rather than by version, and lists new features apart from fixes.
 */
export interface ReleaseNoteMonth {
    /** yyyy-MM */
    month: string;
    features: ReleaseNote[];
    fixes: ReleaseNote[];
}
